/**
 * Coin Service (user-facing)
 *
 * Handles the signed-in user's coin balance and transaction history.
 * Admin coin management (grant/deduct, rate editor) lives in adminService.ts.
 */

import { apiRequest } from '../utils/apiClient';
import { getAuthToken } from '../utils/authToken';

// ============================================================================
// TYPES
// ============================================================================

export type CoinTxnType =
  | 'PLAN_GRANT'
  | 'ADMIN_CREDIT'
  | 'ADMIN_DEBIT'
  | 'PURCHASE_SPEND'
  | 'DISCOUNT_REDEEM'
  | 'REFUND_CREDIT'
  | 'ADJUSTMENT';

export interface CoinTransaction {
  id: string;
  userId: string;
  type: CoinTxnType;
  /** Signed integer: positive for credits, negative for debits. */
  amount: number;
  balanceAfter: number;
  entityType?: string | null;
  entityId?: string | null;
  reason?: string | null;
  adminId?: string | null;
  rateSarPerCoin?: number | null;
  createdAt: string;
}

export interface CoinBalance {
  balance: number;
  rateSarPerCoin: number;
}

export interface CoinHistoryMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CoinHistory {
  data: CoinTransaction[];
  meta: CoinHistoryMeta;
}

/**
 * Get the auth token from the persisted session.
 */
function getToken(): string {
  return getAuthToken();
}

// ============================================================================
// SERVICE METHODS
// ============================================================================

/**
 * Fetch the current user's coin balance and the active SAR-per-coin rate.
 */
export async function getBalance(): Promise<CoinBalance> {
  const response = await apiRequest<{ data: CoinBalance }>('/coins/balance', {
    method: 'GET',
    token: getToken(),
  });
  return response.data;
}

/**
 * Fetch the current user's coin transaction history (paginated).
 */
export async function getHistory(params?: {
  page?: number;
  pageSize?: number;
  type?: CoinTxnType;
}): Promise<CoinHistory> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.pageSize) query.set('pageSize', String(params.pageSize));
  if (params?.type) query.set('type', params.type);
  const qs = query.toString();

  return await apiRequest<CoinHistory>(`/coins/history${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    token: getToken(),
  });
}

const coinService = {
  getBalance,
  getHistory,
};

export default coinService;
